import { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Plus } from 'lucide-react';
import { Reveal } from '@/components/ui/Reveal';

interface QA {
  q: string;
  a: string;
}

const FAQ: QA[] = [
  {
    q: 'Ist das Spiel kostenlos?',
    a: 'Ja, vollständig kostenlos und ohne In-App-Käufe. Keine Werbung im Spielbereich.',
  },
  {
    q: 'Brauche ich einen Account?',
    a: 'Nein. Der Spielstand wird lokal in deinem Browser gespeichert — kein Login, keine Cloud, keine Registrierung.',
  },
  {
    q: 'Welche Städte werden unterstützt?',
    a: 'Alle Städte und Gemeinden in Deutschland, Österreich und der Schweiz, für die OpenStreetMap-Daten existieren — also praktisch alle.',
  },
  {
    q: 'Wie viele Spielstände kann ich haben?',
    a: '3 parallele Spielstände, einzeln benennbar. Jeder mit eigener Stadt, eigenem Modus und eigener Wachen-Struktur.',
  },
  {
    q: 'Funktioniert das auf dem Handy?',
    a: 'Ja, mit angepasstem Mobile-Layout. Optimal sind Tablets und Desktop, weil die Karte mehr Platz braucht.',
  },
  {
    q: 'Was ist der Unterschied zu RescueOperator/Leitstellenspiel?',
    a: 'Dispatch24 ist ein eigenständiges Projekt mit Fokus auf moderne Browser-Technologie und realistische Mechaniken. Es nutzt echte OSM-Daten und implementiert das vollständige BOS-Funkprotokoll.',
  },
  {
    q: 'Kann ich Multiplayer spielen?',
    a: 'Aktuell nur Single-Player. Multiplayer ist für eine spätere Version geplant, aber nicht zugesagt.',
  },
  {
    q: 'Wie kann ich Bugs melden oder Features vorschlagen?',
    a: 'Über den Feedback-Button im Spiel oder per Issue auf GitHub. Jeder Report fließt in die nächste Phase ein.',
  },
];

export function Faq() {
  const [open, setOpen] = useState<number | null>(0);

  return (
    <section id="faq" className="relative px-4 py-24 md:px-6 md:py-32">
      <div className="mx-auto max-w-3xl">
        <Reveal>
          <div className="mb-12 text-center">
            <p className="mb-3 text-sm uppercase tracking-widest text-accent-dispatch">
              Häufige Fragen
            </p>
            <h2 className="font-display text-4xl font-bold leading-tight md:text-5xl">
              Kurz beantwortet.
            </h2>
          </div>
        </Reveal>

        <Reveal>
          <ul className="overflow-hidden rounded-lg border border-border-subtle bg-bg-secondary">
            {FAQ.map((item, i) => {
              const isOpen = open === i;
              return (
                <li
                  key={item.q}
                  className={i > 0 ? 'border-t border-border-subtle' : undefined}
                >
                  <button
                    type="button"
                    onClick={() => setOpen(isOpen ? null : i)}
                    aria-expanded={isOpen}
                    aria-controls={`faq-panel-${i}`}
                    data-testid={`faq-trigger-${i}`}
                    className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left transition hover:bg-bg-elevated/60"
                  >
                    <span className="font-semibold">{item.q}</span>
                    <Plus
                      className={[
                        'h-5 w-5 flex-shrink-0 text-fg-muted transition-transform duration-300',
                        isOpen ? 'rotate-45 text-accent-emergency' : '',
                      ].join(' ')}
                      aria-hidden
                    />
                  </button>
                  <AnimatePresence initial={false}>
                    {isOpen && (
                      <motion.div
                        key="content"
                        id={`faq-panel-${i}`}
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.25, ease: 'easeOut' }}
                        className="overflow-hidden"
                      >
                        <p className="px-5 pb-5 text-sm leading-relaxed text-fg-secondary">
                          {item.a}
                        </p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </li>
              );
            })}
          </ul>
        </Reveal>
      </div>
    </section>
  );
}
